<template>
  <div :class="$style.root">
    <SCAInputBase
      :class="$style.row"
      data-dropdown-anchor
      :alert="alert"
      :focused="focused || open"
      @focusin="focused = true"
      @focusout="focused = false"
      @keydown.enter.prevent="emit('submit')"
    >
      <template #before>
        <button
          type="button"
          tabindex="-1"
          :class="[$style.calendar, open && $style.calendarOpen]"
          :aria-expanded="open"
          test-id="column-filter-date-calendar"
          @click="open = !open"
        >
          <SCASvgIcon :icon="calendarIcon" size="16px" />
        </button>
      </template>
      <SCADatetimePicker
        ref="picker"
        v-model:is-open="open"
        :class="$style.picker"
        :model-value="pickerValue"
        :mode="range ? rangePickerMode : pickerMode"
        :date-format="dateFormat"
        :time-format="timeFormat"
        :date-precision="datePrecision"
        :time-precision="timePrecision"
        :clearable="false"
        appearance="no-border"
        manual-input
        :test-id="range ? 'column-filter-date-range' : 'column-filter-date-start'"
        @update:model-value="onPick"
      />
      <template #after>
        <ConditionRemoveButton :style="removable ? undefined : { visibility: 'hidden' }" @remove="emit('remove')" />
      </template>
    </SCAInputBase>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, shallowRef, watch } from "vue";

import type { DatetimePickerValue, RangeValue } from "../../DatetimePicker/datetimePickerTypes";
import SCADatetimePicker from "../../DatetimePicker/SCADatetimePicker.vue";
import SCAInputBase from "../../SCAInputBase.vue";
import SCASvgIcon from "../../SCASvgIcon.vue";
import ConditionRemoveButton from "../condition/ConditionRemoveButton.vue";
import type { DateFilterKind } from "../types";
import { useDateFilterFormat } from "./useDateFilterFormat";

const props = withDefaults(
  defineProps<{
    kind: DateFilterKind;
    dateFormat: string;
    timeFormat: string;
    datePrecision?: string;
    timePrecision?: string;
    range?: boolean;
    removable?: boolean;
    alert?: boolean;
  }>(),
  { datePrecision: undefined, timePrecision: undefined, range: false, removable: false, alert: false },
);

const emit = defineEmits<{
  remove: [];
  submit: [];
}>();

const start = defineModel<string>("start", { required: true });
const end = defineModel<string>("end", { default: "" });

const { pickerMode, rangePickerMode, toDate, toTerm } = useDateFilterFormat({
  kind: () => props.kind,
  dateFormat: () => props.dateFormat,
  timeFormat: () => props.timeFormat,
  datePrecision: () => props.datePrecision,
  timePrecision: () => props.timePrecision,
});

const calendarIcon = computed(() => (props.kind === "time" ? "phosphor/clock" : "phosphor/calendar-blank"));

const picker = ref<InstanceType<typeof SCADatetimePicker> | null>(null);
const focused = ref(false);
const open = ref(false);

const rangeDraft = shallowRef<RangeValue | null>(null);

const pickerValue = computed<DatetimePickerValue>(() => {
  if (!props.range) return toDate(start.value);
  return rangeDraft.value ?? [toDate(start.value), toDate(end.value)];
});

function commitRange([from, to]: RangeValue) {
  rangeDraft.value = null;
  start.value = toTerm(from);
  end.value = toTerm(to);
}

function onPick(value: DatetimePickerValue) {
  if (!Array.isArray(value)) {
    start.value = toTerm(value);
    return;
  }
  const halfPicked = (value[0] === null) !== (value[1] === null);
  if (open.value && halfPicked) rangeDraft.value = value;
  else commitRange(value);
}

watch(open, (isOpen) => {
  if (!isOpen && rangeDraft.value) commitRange(rangeDraft.value);
});

defineExpose({ focus: () => picker.value?.focus() });
</script>

<style module lang="scss">
@use "../../../styles/icon.scss" as IconMixins;

.root {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.row {
  --sca-input-field-wrapper-padding: 0;
}

.picker {
  --sca-input-field-wrapper-padding: initial;
}

.calendar {
  display: flex;
  flex: none;
  border: none;
  background: transparent;
  @include IconMixins.icon-clickable;
}

.calendar svg {
  @include IconMixins.icon-color(var(--color-content-disabled));
}

.calendar:hover svg,
.calendarOpen svg {
  @include IconMixins.icon-color(var(--color-blue-p1));
}
</style>
